import { useAppState } from '../context/LoadTestContext';
import { useAuth } from '../context/AuthContext';
import { useEffect, useMemo, useState } from 'react';
import tzlookup from '@photostructure/tz-lookup';
import {
  Autocomplete,
  Box,
  Divider,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { fetchProjects, saveBoxProject, setLoadTestJob } from '../services/api';

import { TimerIcon } from './custom_icons';
import CollapsibleCard from './CollapsibleCard';
import CalibrationControl from './CalibrationControl';
import { stepLabel } from './steps';


function Section({ label, value, step = false }) {
  return (
    <Stack direction='column' spacing={step ? 0.5 : 0}>
      <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xl: 12, md: 10, sm: 6 }, whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
      <Typography
        variant='body1'
        textAlign={{ xs: 'left', md: 'center' }}
        sx={{
          fontSize: { xl: 16, lg: 12, md: 10, sm: 8 },
          fontWeight: 500,
          whiteSpace: 'nowrap'
          // whiteSpace: { xs: 'normal', md: 'nowrap' },
          // overflowWrap: 'anywhere',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function LoadingSection({ label, skel_width, step = false }) {
  return (
    <Stack direction='column' spacing={step ? 0.5 : 0} sx={{ minWidth: skel_width }} >
      <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xl: 12, md: 10 } }}>
        {label}
      </Typography>
      <Skeleton variant='rectangular' animation='wave' fullWidth sx={{ borderRadius: 0.5 }} />
    </Stack>
  )
}

function EditableSection({ label }) {
  return (
    <Stack direction='column'>
      <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xl: 12, md: 10 } }}>
        {label}
      </Typography>
      <TextField
        size='small'
        sx={{
          width: 50,
          heigth: 32,
          '& .MuiOutlinedInput-root': {
            height: 32
          },
          '& .MuiOutlinedInput-input': {
            py: 0.5,
            fontSize: { xl: 16, lg: 12, md: 10 }
          },

        }}
      />
    </Stack>
  )
}

function HeaderBar() {

  const { loading, currentReadings, projectDetails, allStepSummaryData, setProjectDetails, selectedBox, loadTest } = useAppState();
  // The step the test is in now, load or unload, whichever steps are shown.
  const currentStep = allStepSummaryData.at(-1);
  const [projects, setProjects] = useState([]);

  const hasProjectDetails = Object.keys(projectDetails || {}).length > 0;

  const getProjectValue = (project, ...keys) => {
    const key = keys.find((candidate) => project?.[candidate] != null);
    return key ? project[key] : '';
  };

  const getProjectLabel = (project) => {
    const name = getProjectValue(project, 'ProjectName', 'projectName');
    const number = getProjectValue(project, 'LongNumber', 'longNumber', 'ProjectNumber', 'projectNumber');
    const city = getProjectValue(project, 'City', 'city');
    const state = getProjectValue(project, 'StateCode', 'stateCode', 'state');

    return [name, number, [city, state].filter(Boolean).join(', ')]
      .filter(Boolean)
      .join(' | ');
  };

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await fetchProjects();
        setProjects(Array.isArray(response) ? response : response.projects || []);
      } catch (err) {
        console.error(err);
      }
    };

    loadProjects();
  }, []);
  const { user } = useAuth() || {};

  // Timezone at the project site, resolved offline from its coordinates. With
  // no project (or no coordinates) fall back to the viewer's preferred
  // timezone, then to the browser's own zone (timeZone: undefined).
  const localTz = useMemo(() => {
    const lat = Number(projectDetails?.Latitude);
    const lon = Number(projectDetails?.Longitude);
    if (Number.isFinite(lat) && Number.isFinite(lon) && (lat !== 0 || lon !== 0)) {
      try {
        return tzlookup(lat, lon);
      } catch (err) {
        console.error(err);
      }
    }
    return user?.timezone || undefined;
  }, [projectDetails, user]);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  const formatNow = (options) => {
    try {
      return now.toLocaleString('en-US', { ...options, timeZone: localTz });
    } catch (err) {
      // A malformed stored timezone would make Intl throw; show browser time.
      return now.toLocaleString('en-US', options);
    }
  };

  const localTime = formatNow({ hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const localDate = formatNow({ month: 'short', day: 'numeric', year: 'numeric' });

  // Desktop keeps the single-row bar; on phones it wraps to several rows, so it
  // folds behind a title like the other cards.
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

  return (
    <CollapsibleCard
      id='project-details'
      title={isMobile ? 'Project Details' : undefined}
      collapsible={isMobile}
      actions={isMobile ? <CalibrationControl /> : undefined}
      sx={{ p: 0, flex: 1, minWidth: 0 }}
      headerSx={{ px: 1.5, pt: 0.75 }}
      bodySx={{ pt: 0 }}
    >
    {/* On desktop the details scroll sideways when they don't fit; the
        Calibration button sits outside that row so it is always in view. */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        backgroundColor: 'background.paper',
        borderRadius: 1,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        flex: 1,
      }}
    >
    <Stack
      direction='row'
      useFlexGap
      divider={<Divider orientation='vertical' flexItem sx={{ alignSelf: 'center', height: '70%', display: { xs: 'none', md: 'block' } }} />}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
        gap: 1,
        minWidth: 0,
        flex: 1,
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'thin',
        pl: { xs: 1.5, md: 2 },
        pr: { xs: 1.5, md: 2 },
        py: 0.5,

        '& > *': {
          flexShrink: 0,
        }
      }}
    >
      {loading ? (
        <LoadingSection label='Project' skel_width={{ xl: 195, lg: 120 }} />
      ) : hasProjectDetails ? (
        <Section label='Project' value={projectDetails.ProjectName} />
      ) : (
        <Stack direction='column'>
          <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xl: 12, md: 10 } }}>
            Project
          </Typography>

          <Autocomplete
            options={projects}
            getOptionLabel={getProjectLabel}
            renderOption={(props, project) => {
              const name = getProjectValue(project, 'ProjectName', 'projectName');
              const number = getProjectValue(project, 'LongNumber', 'longNumber', 'ProjectNumber', 'projectNumber');
              const city = getProjectValue(project, 'City', 'city');
              const state = getProjectValue(project, 'StateCode', 'stateCode', 'state');

              return (
                <Box component='li' {...props} sx={{ ...props.sx, display: 'block' }}>
                  <Stack>
                    <Typography variant='body1' sx={{ fontSize: { xl: 14, md: 12 } }}>
                      {number} - {name}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {city ? `${city}, ${state}` : state}
                    </Typography>
                  </Stack>
                </Box>
              );
            }}
            isOptionEqualToValue={(option, value) => (
              getProjectValue(option, 'Id', 'id') === getProjectValue(value, 'Id', 'id')
            )}
            onChange={(_, project) => {
              if (!project) return;

              const city = getProjectValue(project, 'City', 'city');
              const state = getProjectValue(project, 'StateCode', 'stateCode', 'state');
              const lat = getProjectValue(project, 'Latitude', 'latitude');
              const lon = getProjectValue(project, 'Longitude', 'longitude');
              const projectNumber = getProjectValue(project, 'ProjectNumber', 'projectNumber');
              setProjectDetails({
                ...project,
                ProjectName: getProjectValue(project, 'ProjectName', 'projectName'),
                LongNumber: getProjectValue(project, 'LongNumber', 'longNumber', 'ProjectNumber', 'projectNumber'),
                City: city,
                StateCode: state,
                CityState: [city, state].filter(Boolean).join(', '),
                Latitude: lat,
                Longitude: lon,
              });
              // Persist the selection against the box so it sticks across reloads,
              // and make it the job of the test on screen, so the test keeps it
              // in Historical Data after it ends.
              if (selectedBox?.id && projectNumber) {
                saveBoxProject(selectedBox.id, projectNumber).catch((err) => console.error(err));
              }
              if (loadTest?.id && projectNumber) {
                setLoadTestJob(loadTest.id, { project_number: projectNumber }).catch((err) => console.error(err));
              }
            }}
            sx={{ width: { xl: 260, md: 225 }, height: 32 }}
            renderInput={(params) => (
              <TextField
                {...params}
                // label='Project'
                placeholder='Search projects...'
                size='small'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 32
                  }
                }}
              />
            )}
          />
        </Stack>
      )}

      {loading ? (
        <LoadingSection label='City, State' skel_width={{ xl: 90, lg: 55 }} />
      ) : hasProjectDetails ? (
        <Section label='City, State' value={projectDetails.CityState} />
      ) : null}

      {loading ? (
        <LoadingSection label='Point' skel_width={{ xl: 40, lg: 24 }} />
      ) : projectDetails.Point_Number ? (
        <Section label='Point' value={projectDetails.Point_Number} />
      ) : <EditableSection label='Point' />}

      {loading ? (
        <LoadingSection label='Test' skel_width={{ xl: 24, lg: 16 }} />
      ) : (
        <Section label='Test' value={projectDetails?.Test_Number || 1} />
      )}

      <Box
        sx={{
          display: 'flex',
          height: { xs: 'auto', md: '100%' },
          alignItems: 'center',
          gap: { xl: 4, lg: 2, sm: 1, xs: 1 },
          border: '2px solid #0ea5e9',
          borderRadius: 1,
          px: { xl: 2, lg: 1, sm: 0.5, xs: 1 },
          py: { xl: 1, lg: 0.5, xs: 0.5 },
          minWidth: 125
        }}
      >
        <Stack direction='row' alignItems='center' spacing={{ xl: 1.5, lg: 1 }}>
          <TimerOutlinedIcon color='primary' sx={{ fontSize: { xl: 35, lg: 24, sm: 20 } }} />
          {loading ? <LoadingSection label='STEP' skel_width={{ xl: 24, lg: 16 }} step={true} /> : <Section label='Step' value={currentStep ? stepLabel(currentStep) : undefined} step={true} />}
        </Stack>

        <Divider orientation='vertical' flexItem sx={{ alignSelf: 'center', height: '70%' }} />

        {loading ? <LoadingSection label='TIME IN STEP' skel_width={{ xl: 75, lg: 58 }} step={true} /> : <Section label='Time In Step' value={currentStep?.step_duration_display} step={true} />}
      </Box>


      <Stack direction='row' alignItems='center' spacing={{ xl: 1.5, lg: 1 }}>
        <TimerOutlinedIcon color='primary' sx={{ fontSize: { xl: 35, lg: 24 } }} />
        {loading ? <LoadingSection label='Test Duration' animation='wave' skel_width={{ xl: 75, lg: 56 }} /> : <Section label='Test Duration' value={currentReadings.test_duration} />}
      </Stack>

      <Stack direction='row' alignItems='center' spacing={{ xl: 1.5, lg: 1 }}>
        <AccessTimeIcon color='primary' sx={{ fontSize: { xl: 35, lg: 24 } }} />

        <Stack direction='column'>
          <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xl: 12, md: 10, sm: 8 }, whiteSpace: 'nowrap' }}>Local Time</Typography>
          {loading ? <Skeleton variant='rectangular' animation='wave' fullwidth sx={{ mb: 0.25, borderRadius: 0.5, height: { xl: 24, md: 14 } }} /> : <Typography sx={{ fontSize: { xl: 16, lg: 12, md: 10, sm: 8 }, fontWeight: 500 }}>{localTime}</Typography>}
          {loading ? <Skeleton variant='rectangular' animation='wave' fullwidth sx={{ borderRadius: 0.5, height: { xl: 20, md: 14 } }} /> : <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xl: 12, md: 10, sm: 8 } }}>{localDate}</Typography>}
        </Stack>
      </Stack>

    </Stack>
    {!isMobile && (
      <>
        <Divider orientation='vertical' flexItem sx={{ alignSelf: 'center', height: '70%' }} />
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, px: { md: 1, xl: 2 } }}>
          <CalibrationControl />
        </Box>
      </>
    )}
    </Box>
    </CollapsibleCard>
  );
}

export default HeaderBar;
